import type { TabNavMenuStyleOverrides, TabNavOverrides } from '@withjoy/joykit/components/TabNav';
import { pxToRem } from '@withjoy/joykit/theme';

export const ADMIN_TAB_NAV_OVERRIDES: TabNavOverrides = {
  List: {
    props: {
      borderBottomWidth: 0,
    },
  },
  Link: {
    props: {
      _hover: { color: 'newUIBrandPrimary' },
    },
  },
  MenuTrigger: {
    props: {
      _hover: { color: 'newUIBrandPrimary' },
    },
  },
};

// Matches AdminHeaderBar's Root `paddingLeft/Right={5}` (space[5] = 16px), the gutter the mobile
// tab row sits inside. Keep the two in sync or the tabs stop lining up with the hamburger.
const HEADER_GUTTER_PX = 16;
// TabNav's own default-variant list padding (space[2]). `paddingX` below replaces it, so it is added back.
const TAB_LIST_PADDING_PX = 6;

// On phones RSVP's four tabs (~431px) are wider than the screen. Left alone they widened the whole page,
// which then scrolled sideways with "Questions" cut off at the left edge. Constraining the nav to its
// row and letting the list scroll keeps every tab reachable inside the row — the same treatment as
// Stationery's CardsTabNav. From sm2 (768px) up the tabs fit, so nothing changes there.
//
// The scroll area bleeds out through the header's gutter (negative margin, widened by both gutters)
// and puts the gutter back as list padding. At rest the first tab still lines up with the hamburger,
// but scrolled tabs run to the screen edge instead of being clipped 16px short of it.
export const ADMIN_SCROLLING_TAB_NAV_OVERRIDES: TabNavOverrides = {
  ...ADMIN_TAB_NAV_OVERRIDES,
  Root: {
    props: {
      width: { _: `calc(100% + ${pxToRem(HEADER_GUTTER_PX * 2)})`, sm2: 'auto' },
      marginX: { _: pxToRem(-HEADER_GUTTER_PX), sm2: 0 },
      minWidth: 0,
    },
  },
  List: {
    props: {
      ...ADMIN_TAB_NAV_OVERRIDES.List?.props,
      overflowX: { _: 'auto', sm2: 'visible' },
      paddingX: { _: pxToRem(HEADER_GUTTER_PX + TAB_LIST_PADDING_PX), sm2: pxToRem(TAB_LIST_PADDING_PX) },
      style: { scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' },
    },
  },
};

export const ADMIN_MENU_STYLE_OVERRIDES: TabNavMenuStyleOverrides = {
  popoverContent: {
    borderRadius: 4,
    boxShadow: '0 0.4375rem 1.6875rem rgba(44, 41, 37, 0.06), 0 0.25rem 1rem rgba(0, 0, 0, 0.07)',
  },
  popoverBody: {
    minWidth: pxToRem(260),
    maxWidth: pxToRem(320),
    maxHeight: 'calc(100dvh - 8rem)',
    overflow: 'auto',
    padding: 2,
  },
  menuItem: {
    gap: 5,
    paddingY: 2,
    paddingX: 3,
    typographyVariant: 'label2',
    borderRadius: '12px',
    minHeight: '36px',
    _hover: {
      backgroundColor: 'mono2',
      color: 'newUIBrandPrimary',
    },
  },
  menuBadge: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'newUIBrandPrimary',
    border: '1px solid',
    borderColor: 'newUIBrandPrimary',
    padding: '1px 5px',
    fontSize: pxToRem(10),
    lineHeight: 1.4,
    letterSpacing: '-0.032px',
  },
  menuDivider: {
    as: 'hr',
    margin: 3,
    width: 'auto',
    height: 'auto',
    backgroundColor: 'transparent',
    borderTop: '1px solid',
    borderColor: 'mono3',
    marginY: 3,
  },
};
